import React, { useState, useEffect, useRef } from 'react';
import { Bot, Send, Brain, Cpu, HeartPulse } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import DashboardLayout from '../components/DashboardLayout';
import { aiAPI, portfolioAPI, expenseAPI, incomeAPI } from '../services/apiServices';

const COLORS = {
  primary: '#FB7185', // Professional Rose-400
  secondary: '#F472B6', // Warm Pink-400
  accent: '#FDA4AF', // Soft Rose-300
  lightPink: 'rgba(251, 113, 133, 0.08)',
  glassPink: 'rgba(255, 255, 255, 0.03)',
  borderPink: 'rgba(251, 113, 133, 0.2)'
};

// === 3D TILT CARD COMPONENT ===
const TiltCard = ({ children, className, depth = 5, glowColor = "rgba(251, 113, 133, 0.12)" }) => {
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const card = cardRef.current;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = ((y - centerY) / centerY) * -depth;
    const rotateY = ((x - centerX) / centerX) * depth;

    card.style.setProperty('--mouse-x', `${x}px`);
    card.style.setProperty('--mouse-y', `${y}px`);
    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
  };

  const handleMouseLeave = () => {
    if (!cardRef.current) return;
    cardRef.current.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg)`;
  };

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={`relative transition-all duration-300 ease-out preserve-3d group ${className}`}
      style={{
        background: `radial-gradient(600px circle at var(--mouse-x) var(--mouse-y), ${glowColor}, transparent 40%)`
      }}
    >
      {children}
    </div>
  );
};

// === TYPEWRITER EFFECT ===
const TypewriterText = ({ text, speed = 10 }) => {
  const [displayedText, setDisplayedText] = useState("");
  useEffect(() => {
    let i = 0;
    const interval = setInterval(() => {
      setDisplayedText(text.slice(0, i + 1));
      i++;
      if (i >= text.length) clearInterval(interval);
    }, speed);
    return () => clearInterval(interval);
  }, [text, speed]);
  return <span>{displayedText}</span>;
};

// === PULSE RATE COMPONENT ===
const PulseRate = ({ isFlat }) => {
  return (
    <div className="flex items-center justify-center w-32 h-10 overflow-hidden bg-rose-950/20 rounded-xl border border-rose-500/10 shadow-inner">
      <svg viewBox="0 0 100 40" className="w-full h-full">
        <path
          d={isFlat ? "M 0 20 L 100 20" : "M 0 20 L 20 20 L 25 10 L 30 30 L 35 20 L 60 20 L 65 5 L 70 35 L 75 20 L 100 20"}
          fill="none"
          stroke={COLORS.primary}
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
          className={isFlat ? "flat-transition" : "pulse-path"}
        />
      </svg>
    </div>
  );
};

const AITools = () => {
  const [budgetChat, setBudgetChat] = useState([
    { role: 'assistant', content: 'Ask a Finance Related query to me..' }
  ]);
  const [budgetInput, setBudgetInput] = useState('');
  const [budgetLoading, setBudgetLoading] = useState(false);
  const [isFlat, setIsFlat] = useState(false);
  const [status, setStatus] = useState('checking'); // 'active', 'offline', 'checking'

  // Ensure page starts at top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // AI Status Check
  useEffect(() => {
    const checkApiStatus = async () => {
      try {
        const isOnline = await aiAPI.checkStatus();
        setStatus(isOnline ? 'active' : 'offline');
      } catch (e) {
        setStatus('offline');
      }
    };
    checkApiStatus();
  }, []);

  // Auto-scroll for chat - Only if more than initial greeting
  const chatEndRef = useRef(null);
  useEffect(() => {
    if (budgetChat.length > 1) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [budgetChat]);

  // Fetch financial context for better AI advice
  const [financialContext, setFinancialContext] = useState(null);

  useEffect(() => {
    const fetchContext = async () => {
      try {
        const [portfolio, expenses, incomes] = await Promise.all([
          portfolioAPI.getPortfolio().catch(e => []),
          expenseAPI.getExpenses().catch(e => []),
          incomeAPI.getIncome().catch(e => [])
        ]);

        const networth = portfolio.reduce((sum, item) => sum + (Number(item.quantity) * Number(item.current_price || item.buy_price)), 0);
        const monthlyExpenses = expenses.reduce((sum, item) => sum + Number(item.amount), 0);
        const monthlyIncome = incomes.reduce((sum, item) => sum + Number(item.amount), 0);

        setFinancialContext({
          networth: `₹${networth.toLocaleString('en-IN')}`,
          monthlyExpenses: `₹${monthlyExpenses.toLocaleString('en-IN')}`,
          monthlyIncome: `₹${monthlyIncome.toLocaleString('en-IN')}`,
          expenseSummary: expenses.slice(0, 5).map(e => `${e.category}: ₹${e.amount}`).join(", ")
        });
      } catch (e) {
        console.warn("[AI Tools] Failed to fetch context for advisor");
      }
    };
    fetchContext();
  }, []);

  const handleBudgetChat = async (e) => {
    e.preventDefault();
    if (!budgetInput.trim()) return;

    const userMessage = budgetInput;
    setBudgetChat([...budgetChat, { role: 'user', content: userMessage }]);
    setBudgetInput('');
    setBudgetLoading(true);
    setIsFlat(false);

    try {
      const response = await aiAPI.getFinancialAdvice(userMessage, financialContext);

      // Artificial delay to show 'flatline' effect before answering
      setTimeout(() => {
        setIsFlat(true);
        setTimeout(() => {
          const cleanAnswer = response.answer.replace(/\*\*/g, '');
          setBudgetChat(prev => [...prev, { role: 'assistant', content: cleanAnswer }]);
          setBudgetLoading(false);
          setIsFlat(false);
        }, 800);
      }, 1500);

    } catch (error) {
      setBudgetChat(prev => [...prev, { role: 'assistant', content: "Interface sync failed. Please attempt a manual reconnect." }]);
      setBudgetLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-[#020617] p-4 md:p-8 font-sans selection:bg-rose-500/30 overflow-x-hidden">
        {/* Header Section */}
        <div className="mb-8 flex flex-col items-start overflow-hidden">
          <div className="mb-3 flex items-center gap-2 bg-rose-500/5 px-2 py-0.5 rounded-full border border-rose-500/10 animate-in fade-in slide-in-from-top-10 duration-1000">
            <Cpu size={10} className="text-[#FB7185]" />
            <span className="text-[8px] font-black uppercase tracking-[0.3em] text-[#FB7185]">Neural Terminal Sync</span>
          </div>
          <h1 className="text-xl md:text-3xl font-black tracking-tighter text-[#F1F5F9] mb-3 uppercase animate-in fade-in slide-in-from-left-10 duration-1000 delay-300 leading-none">
            Finance <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-400 via-rose-500 to-rose-600">Chatbot</span>
          </h1>
          <p className="text-[#94A3B8] text-[13px] max-w-2xl font-medium leading-relaxed animate-in fade-in slide-in-from-bottom-6 duration-1000 delay-700 opacity-80">
            Connect to our institutional-grade neural network for deep financial analysis, risk assessment, and real-time market intelligence.
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          {/* Chat Container with Animated Border */}
          <div className="relative group/chatbot animate-in fade-in zoom-in-95 duration-700">
            {/* Animated Glow Border */}
            <div className="absolute -inset-[1px] bg-gradient-to-r from-rose-500/0 via-rose-500/40 to-rose-500/0 rounded-[2.5rem] opacity-70 group-hover/chatbot:opacity-100 transition-opacity blur-[2px] animate-shimmer-border"></div>

            <Card className="p-0 h-[600px] md:h-[750px] flex flex-col bg-[#0B1120]/90 backdrop-blur-3xl border-rose-500/10 rounded-[1.5rem] md:rounded-[2.5rem] shadow-[0_0_60px_rgba(251,113,133,0.1)] relative overflow-hidden z-10 transition-all duration-500">
              {/* Top Pink Gradient Effect */}
              <div className="absolute top-0 left-0 w-full h-[180px] bg-gradient-to-b from-rose-500/[0.08] to-transparent pointer-events-none"></div>

              {/* Chat Header */}
              <div className="p-6 md:p-10 border-b border-rose-500/10 flex flex-col md:flex-row items-center justify-between gap-6 relative z-10">
                <div className="flex items-center space-x-6">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-rose-400 to-rose-600 rounded-[1rem] md:rounded-[1.5rem] flex items-center justify-center text-white shadow-[0_0_30px_rgba(251,113,133,0.3)] animate-pulse border-t border-white/20">
                    <Bot size={28} className="md:w-8 md:h-8" />
                  </div>
                  <div>
                    <h2 className="text-xl md:text-2xl font-black text-white uppercase tracking-tight leading-none text-glow-pink">AI Neural Advisor</h2>
                    <p className="text-[10px] md:text-[12px] font-black text-rose-400/80 mt-2 uppercase tracking-[0.2em]">Institutional Knowledge Base</p>
                  </div>
                </div>
                <div className="hidden md:flex items-center gap-3 bg-rose-500/10 px-4 py-2 rounded-xl border border-rose-500/20">
                  <span className={`w-2 h-2 rounded-full animate-ping ${status === 'active' ? 'bg-emerald-400' : status === 'offline' ? 'bg-rose-500' : 'bg-amber-400'}`}></span>
                  <span className="text-[10px] font-black text-rose-300 uppercase tracking-widest text-glow-pink">
                    {status === 'active' ? 'Interface: Active' : status === 'offline' ? 'Neural Sync Failure' : 'Initializing...'}
                  </span>
                </div>
              </div>

              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto px-4 md:px-10 py-6 md:py-10 space-y-6 md:space-y-8 custom-scrollbar relative bg-[radial-gradient(circle_at_center,rgba(251,113,133,0.05)_0,transparent_100%)]">
                {budgetChat.map((message, index) => (
                  <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-2 duration-500`}>
                    <div className="flex gap-4 max-w-[90%] md:max-w-[70%]">
                      {message.role === 'assistant' && (
                        <div className="w-8 h-8 md:w-11 md:h-11 rounded-lg md:rounded-xl bg-rose-500/15 flex items-center justify-center text-rose-300 border border-rose-500/20 flex-shrink-0 shadow-[0_0_15px_rgba(251,113,133,0.1)]">
                          <Brain size={16} className="md:w-5 md:h-5" />
                        </div>
                      )}
                      <div className={`p-4 md:p-6 rounded-[1.5rem] md:rounded-[2rem] shadow-xl ${message.role === 'user'
                        ? 'bg-gradient-to-br from-rose-500 to-rose-700 text-white rounded-br-none shadow-rose-900/30'
                        : 'bg-rose-500/[0.06] backdrop-blur-3xl text-slate-100 border border-rose-500/15 rounded-bl-none shadow-[inset_0_1px_1px_rgba(255,255,255,0.03)] ring-1 ring-white/5'
                        }`}>
                        <div className="text-[14px] leading-relaxed font-medium whitespace-pre-wrap">
                          {message.role === 'assistant' && index === budgetChat.length - 1 && budgetLoading === false ? (
                            <TypewriterText text={message.content} speed={10} />
                          ) : message.content}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {budgetLoading && (
                  <div className="flex justify-start animate-in fade-in duration-300">
                    <div className="flex gap-4 items-center">
                      <div className="w-11 h-11 rounded-xl bg-rose-500/15 flex items-center justify-center text-rose-300 border border-rose-500/20 flex-shrink-0 animate-pulse">
                        <Brain size={20} className={isFlat ? "" : "animate-bounce"} />
                      </div>
                      <div className="bg-rose-500/[0.06] backdrop-blur-3xl px-6 py-4 rounded-3xl rounded-bl-none border border-rose-500/15 flex flex-col gap-3 shadow-[inset_0_1px_1px_rgba(255,255,255,0.03)]">
                        <PulseRate isFlat={isFlat} />
                        <span className="text-[10px] font-black text-rose-300/60 uppercase tracking-[0.2em]">
                          {isFlat ? "Neural Locked" : "Synthesizing Intelligence..."}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Input Area */}
              <div className="p-8 bg-[#0B1120]/80 backdrop-blur-3xl border-t border-rose-500/10 relative">
                <form onSubmit={handleBudgetChat} className="flex gap-4 items-center max-w-5xl mx-auto">
                  <div className="relative flex-1 group">
                    <Input
                      placeholder="Ask finance related Query..."
                      value={budgetInput}
                      onChange={(e) => setBudgetInput(e.target.value)}
                      className="w-full h-16 bg-white/[0.03] border-white/10 focus:border-rose-400/40 focus:ring-rose-400/10 text-white text-sm px-8 rounded-2xl transition-all shadow-inner placeholder:text-[#94A3B8]/30"
                    />
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-focus-within:opacity-100 transition-opacity">
                      <span className="text-[10px] text-rose-400 font-extrabold tracking-widest uppercase text-glow-pink">Stream: Active</span>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={budgetLoading || !budgetInput.trim()}
                    className={`h-16 w-16 rounded-2xl transition-all border-t border-white/10 ${budgetInput.trim()
                      ? 'bg-rose-500 hover:bg-rose-600 text-white shadow-[0_10px_25px_-5px_rgba(251,113,133,0.4)] scale-100 active:scale-95'
                      : 'bg-white/5 text-slate-500 scale-95 opacity-50'
                      }`}
                  >
                    <Send size={24} className={budgetInput.trim() ? 'animate-pulse' : ''} />
                  </Button>
                </form>
              </div>
            </Card>
          </div>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        .font-sans { font-family: 'Plus Jakarta Sans', sans-serif !important; }
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(251, 113, 133, 0.1); border-radius: 20px; }
        .preserve-3d { transform-style: preserve-3d; }
        
        @keyframes shimmer-border {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }

        .animate-shimmer-border {
          background-size: 200% auto;
          animation: shimmer-border 6s linear infinite;
        }

        .pulse-path {
          stroke-dasharray: 200;
          stroke-dashoffset: 200;
          animation: pulse-dash 2s infinite linear;
        }

        @keyframes pulse-dash {
          0% { stroke-dashoffset: 400; }
          100% { stroke-dashoffset: 0; }
        }

        .flat-transition {
          transition: d 0.5s ease-in-out;
        }

        .text-glow-pink {
          text-shadow: 0 0 10px rgba(251, 113, 133, 0.4);
        }
      `}</style>
    </DashboardLayout>
  );
};

export default AITools;